'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createChat,
  getNextCaseNumber,
  sendMessageToChat,
  uploadChatImage,
} from '@util/firestore/messages';
import { updateOrderByKey } from '@util/firestore/orders';
import { getProductsByIds } from '@util/firestore/products';
import { getUserById } from '@util/firestore/users';
import useRealtimeChats from '@util/hooks/useRealtimeChats';
import { getCdnImageUrls, slugify } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { ChatMessageInput, chatMessagesSchema } from 'models/chat';
import { OrderDocument } from 'models/order';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface UseHelpWithOrderProps {
  order?: OrderDocument | null;
  dismiss?: () => void;
  variant?: 'buyer' | 'seller';
}
const formSchema = z
  .object({
    issue: z.string({
      required_error: 'Please select an issue',
    }),
    subissue: z.string().optional(),
    item_id: z.string().optional(),
    comment: z.string().optional(),
    images: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      (data.issue === 'Something Else' ||
        data.issue === "Something's wrong with my order") &&
      !data.comment
    ) {
      ctx.addIssue({
        path: ['comment'],
        code: z.ZodIssueCode.custom,
        message: 'Please enter a comment',
      });
    }
  });

type Form = z.infer<typeof formSchema>;

export const useHelpWithOrder = ({
  order,
  dismiss,
  variant = 'buyer',
}: UseHelpWithOrderProps) => {
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [trackModalOpen, setTrackModalOpen] = useState(false);
  const { userDoc } = useAuth();
  const { realtimeChats } = useRealtimeChats(userDoc?.uid);
  const { showErrorToast } = useToastContext();
  const queryClient = useQueryClient();
  const router = useRouter();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setError,
    setValue,
    formState: { errors },
  } = useForm<Form>({
    mode: 'onSubmit',
    resolver: zodResolver(formSchema),
  });

  const form = watch();
  const selectedSeller = Object.values(order?.sellers ?? {})
    .flat()
    .find((item) => item.id === form.item_id);
  const { data: seller } = useQuery({
    queryKey: ['user', selectedSeller?.seller_id],
    queryFn: () => getUserById(selectedSeller?.seller_id!),
    enabled: !!selectedSeller?.seller_id,
  });
  const { data: products } = useQuery({
    queryKey: ['products', order?.id],
    queryFn: () =>
      getProductsByIds(
        Object.values(order?.sellers ?? {})
          .flat()
          .map((item) => item.id)
      ),
    enabled: !!order?.id,
  });

  const onSubmit = async (data: Form, messageTo: 'support' | 'seller') => {
    if (!userDoc || !order) return;
    if (messageTo === 'seller' && !seller) {
      showErrorToast(
        'Issue sending message to seller. Please contact support instead.'
      );
      return;
    }
    const messageToId =
      messageTo === 'support'
        ? process.env.NEXT_PUBLIC_SUPPORT_ID!
        : seller?.uid!; // validated above
    setLoadingSubmit(true);
    const promises: Promise<any>[] = [];
    let content = '';
    let commentContent = '';
    if (data.issue === 'Request Cancellation') {
      content = `Support chat initiated with the following issue: Request Cancellation`;
      const product = products?.find((item) => item.id === data.item_id);
      content += ` for ${product?.title || `Order #${order?.order_num}`}`;
      if (data.subissue) {
        content += ` due to: "${data.subissue}"`;
      }
      if (data.comment) {
        commentContent += `Comment: ${data.comment}`;
      }
    } else {
      content =
        messageTo === 'support'
          ? `Support chat initiated with the following issue: ${
              data.subissue || data.issue
            }`
          : `I need help with my order #${
              order?.order_num
            } with the following issue: ${data.subissue || data.issue}.`;
      if (data.comment) {
        commentContent += `Comment: ${data.comment}`;
      }
      const product = Object.values(order?.sellers ?? {})
        .flat()
        .find((item) => item.id === data.item_id)?.product_name;
      if (product) content += `\n Product: ${product}`;
    }

    const message = {
      uid: userDoc.uid,
      content,
      created_at: Date.now(),
    } as ChatMessageInput;
    const commentMessage = {
      uid: userDoc.uid,
      content: commentContent,
      created_at: Date.now(),
    } as ChatMessageInput;
    const messageParsed = chatMessagesSchema.parse(message);
    let commentMessageParsed = null;
    if (commentContent) {
      commentMessageParsed = chatMessagesSchema.parse(commentMessage);
    }
    const holdIssueList = ['Something Else', "It's different than expected"];

    const shouldHoldOrder =
      (variant === 'buyer' &&
        holdIssueList.includes(data.issue) &&
        messageTo === 'support') ||
      data.issue === 'Request Cancellation';

    if (shouldHoldOrder) {
      promises.push(updateOrderByKey(order.id, 'total_state', 3));
    }

    const existingOrderChat = realtimeChats?.find(
      (chat) =>
        chat.order_id &&
        chat.order_id === order?.id &&
        chat.uids?.includes(messageToId) &&
        chat.uids?.includes(userDoc.uid)
    );
    const chat =
      existingOrderChat ??
      (await createChat({
        is_offer: false,
        uids: [userDoc.uid, messageToId],
        order_num: order.order_num,
        order_id: order.id,
        ...(data.item_id && { product_id: data.item_id }),
        buyer_id: variant === 'buyer' ? userDoc.uid : messageToId,
        seller_id: variant === 'buyer' ? messageToId : userDoc.uid,
        last_time: Date.now(),
        created_at: Date.now(),
        messages: [],
        ...(messageTo === 'support' &&
          !['Help with shipping', 'When will I get paid?'].includes(
            data.subissue || data.issue
          ) && {
            case_num: await getNextCaseNumber(),
          }),
        from_web: true,
        is_auto: ['Help with shipping', 'When will I get paid?'].includes(
          data.subissue || data.issue
        ),
        unread: {
          [messageToId]: true,
        },
        id: '', // placeholder for id, get's created in the createChat function
      }));
    const messages = [messageParsed];

    if (commentMessageParsed) {
      messages.push(commentMessageParsed);
    }
    if (form.images) {
      const uploaded_images = await Promise.all(
        form.images.map(async (image) => {
          const imageBlob = await fetch(image).then((res) => res.blob());
          return uploadChatImage(imageBlob, userDoc.uid, chat.id);
        })
      );

      for (const image of uploaded_images) {
        const cdnUrl = getCdnImageUrls(image.path);
        const message = {
          uid: userDoc.uid,
          content: cdnUrl.full,
          download_url: image.download_url,
          created_at: Date.now(),
          is_img: true,
          is_auto: false,
        } as ChatMessageInput;
        const messageParsed = chatMessagesSchema.parse(message);
        messages.push(messageParsed);
      }
    }
    promises.push(sendMessageToChat(messages, chat, userDoc.uid));
    await Promise.all(promises);

    // reset the form
    setValue('comment', '');
    setValue('images', []);
    setValue('issue', '');
    setValue('subissue', '');
    setLoadingSubmit(false);
    queryClient.invalidateQueries({
      queryKey: ['order', order.id],
    });
    router.push(`/dashboard/messages/${chat.id}`);
    dismiss?.();
  };

  const getOptions = () => {
    if (variant === 'buyer') {
      const options = [];
      if (
        Object.values(order?.sellers ?? {})
          .flat()
          .some((item) => item.state !== 3)
      ) {
        options.push(`Where's my package?`);
      }
      if (
        Object.values(order?.sellers ?? {})
          .flat()
          .some((item) => item.state === 0) &&
        !order?.offer_id
      ) {
        options.push('Request Cancellation');
      }
      if (
        Object.values(order?.sellers ?? {})
          .flat()
          .some((item) => item.state === 2 || item.state >= 4)
      ) {
        options.push("It's different than expected");
      }
      options.push('Something Else');
      return options.map((issue) => ({
        label: issue,
        value: issue,
        id: slugify(issue),
      }));
    } else {
      return [
        'Item no longer in stock',
        'Help with shipping',
        'When will I get paid?',
        'Something Else',
      ].map((issue) => ({
        label: issue,
        value: issue,
        id: slugify(issue),
      }));
    }
  };

  const getSubOptions = (issue: string) => {
    const subOptions = [];
    if (issue === "It's different than expected") {
      subOptions.push(
        `It's damaged or defective`,
        `It's missing items or parts`,
        `Item doesn't match description`
      );
      subOptions.push('Something Else');
    } else if (issue === 'Request Cancellation') {
      subOptions.push(
        `Ordered by mistake / No longer needed`,
        'Seller not responding',
        'Seller not responding / Has not shipped',
        'Found a better price elsewhere',
        'Ordered wrong size / fitment'
      );
    } else if (issue === 'Help with shipping') {
      subOptions.push(
        'Add/Edit Tracking Number',
        'Shipping Label Issues',
        'Carrier Lost or Delayed Package',
        'Other Shipping Issue'
      );
    }
    return subOptions.map((issue) => ({
      label: issue,
      value: issue,
      id: slugify(issue),
    }));
  };

  const selectedItemHasTracking = Object.values(order?.sellers ?? {})
    .flat()
    .find((i) => i.id === form.item_id && i.tracking_number);

  return {
    loadingSubmit,
    trackModalOpen,
    setLoadingSubmit,
    setTrackModalOpen,
    register,
    control,
    handleSubmit,
    setError,
    setValue,
    errors,
    formSchema,
    onSubmit,
    selectedItemHasTracking,
    getOptions,
    getSubOptions,
    seller,
    form,
  };
};
